import { connectSocket, socket } from "./core/socket";
import { startClockSync } from "./core/clockSync";
import { store } from "./core/store";
import { canvasBus } from "./core/canvasBus";
import { audioManager } from "./core/audio";
import { themeManager } from "./core/theme";
import { showToast } from "./core/toast";
import { loadIdentity, clearIdentity } from "./core/storage";
import { initLobbySelect } from "./ui/lobbySelect";
import { initLayout } from "./ui/layout";
import { initPlayerList } from "./ui/playerList";
import { initChat } from "./ui/chat";
import { initMainArea } from "./ui/mainArea";
import { initSettingsPanel } from "./ui/settingsPanel";
import { initThemePanel } from "./ui/themePanel";
import { initHowToPlay } from "./ui/howToPlay";
import { initSessionBanner } from "./ui/sessionBanner";

function wireSocket(lobbyCode: string): void {
  socket.on("connect", () => {
    store.setState({ connected: true });
    const identity = loadIdentity(lobbyCode);
    if (identity) {
      socket.emit(
        "reconnect:request",
        { playerId: identity.playerId, reconnectToken: identity.reconnectToken },
        (res) => {
          if (res.ok) {
            store.setState({ joined: true, myPlayerId: identity.playerId, isSpectator: !!res.isSpectator });
          } else {
            clearIdentity(lobbyCode);
            store.setState({ joined: false, myPlayerId: null, isHost: false });
          }
        }
      );
    }
  });

  socket.on("disconnect", () => {
    store.setState({ connected: false });
  });

  socket.on("game:snapshot", (snapshot) => {
    store.setState({ snapshot });
    const me = snapshot.players.find((p) => p.id === store.getState().myPlayerId);
    if (me) store.setState({ isHost: me.isHost, isSpectator: me.isSpectator });
  });

  socket.on("word:view", (view) => store.setState({ wordView: view }));
  socket.on("wordSelection:offer", (offer) => store.setState({ wordOffer: offer }));
  socket.on("ccc:offer", (offer) => store.setState({ cccOffer: offer }));
  socket.on("config:current", (config) => store.setState({ config }));
  socket.on("server:info", (info) =>
    store.setState({ cccDebugAvailable: info.cccDebugAvailable, availableWordLists: info.availableWordLists })
  );

  socket.on("chat:message", (message) => store.pushChatMessage(message));
  socket.on("chat:private", (message) => store.pushChatMessage(message));
  socket.on("chat:history", (messages) => store.setState({ chatMessages: messages.slice() }));

  socket.on("canvas:event", (event) => canvasBus.emitEvent(event));
  socket.on("canvas:sync", (items) => canvasBus.emitSync(items));
  socket.on("ccc:blindness", (cycle) => canvasBus.emitBlindness(cycle));

  socket.on("sound:cue", (cue) => audioManager.play(cue));

  socket.on("victory:started", () =>
    store.setState({
      victoryAchievements: [],
      victoryReveals: [],
      victoryFinished: false,
      victoryVotes: { playAgain: 0, returnToLobby: 0 },
    })
  );
  socket.on("achievement:shown", (achievement) => {
    store.setState({
      victoryAchievements: [...store.getState().victoryAchievements, { ...achievement, playerIds: [] }],
    });
  });
  socket.on("achievement:earned", ({ key, playerId }) => {
    // Always the most recently shown achievement - the server never starts the
    // next one until the current one's earners have all appeared.
    const achievements = store.getState().victoryAchievements.slice();
    const index = achievements.map((a) => a.key).lastIndexOf(key);
    if (index < 0) return;
    const current = achievements[index]!;
    if (current.playerIds.includes(playerId)) return;
    achievements[index] = { ...current, playerIds: [...current.playerIds, playerId] };
    store.setState({ victoryAchievements: achievements });
  });
  socket.on("victory:reveal", (entry) => {
    store.setState({ victoryReveals: [...store.getState().victoryReveals, entry] });
  });
  socket.on("victory:finished", () => store.setState({ victoryFinished: true }));
  socket.on("victory:votes", (votes) => store.setState({ victoryVotes: votes }));

  socket.on("error:toast", (message) => showToast(message));
}

function startGameApp(lobbyCode: string): void {
  document.getElementById("lobby-select-screen")!.classList.add("hidden");
  document.getElementById("app")!.classList.remove("hidden");

  store.setState({ lobbyCode });
  connectSocket(lobbyCode, () => {
    // The lobby vanished (reaped for being idle/inactive, or the code was never
    // valid) - a full reload is simpler and safer than trying to unwind an
    // already-live game UI's store subscriptions back to a clean slate in
    // place. Delayed briefly so a just-shown toast (e.g. "Lobby closed due to
    // inactivity.") has time to actually be read before the page navigates out
    // from under it - reload() would otherwise typically win the race, since
    // the client's own reconnect attempt (and the "lobby-gone" rejection that
    // triggers this callback) both happen well within the toast's 4s display.
    setTimeout(() => {
      window.location.href = "/";
    }, 3000);
  });
  startClockSync(socket);
  wireSocket(lobbyCode);
  initLayout();
  initPlayerList();
  initChat();
  initMainArea();
  initSettingsPanel();
  initThemePanel();
  initHowToPlay();
  initSessionBanner();
}

function main(): void {
  themeManager.applyStored();
  initLobbySelect(document.getElementById("lobby-select-screen")!, startGameApp);
}

main();
